// `chrome-extension/services/enrichment/enrichment.service.ng1.ts` — whether a candidate's
// work-experience card carries the green "Validated by Woo …" line, and which date it quotes.

/** The three date fields the fallback below reads, plus the version that picks between them. */
export interface EnrichableEntity {
  indexVersion?: number | null
  validationDate?: number | null
  lastRelevancyDate?: number | null
  enrichmentRelevancyDate?: number | null
  enrichedExtraExperience?: boolean | null
  enrichedRecordsAdded?: number | null
}

/**
 * `getValidationDateWithFallback`.
 *
 * ⚠️ THE FALLBACK IS KEYED ON `indexVersion`, NOT ON WHETHER `validationDate` IS SET. A
 * candidate indexed at version 1 or later answers with `validationDate` even when that is
 * absent — reading the older fields as a general fallback would put a date on rows production
 * leaves blank. Upstream's own comment says the branch goes away a deployment after tagging.
 */
export function validationDateWithFallback(entity: EnrichableEntity): number | undefined {
  if (entity.indexVersion !== undefined && entity.indexVersion !== null && entity.indexVersion >= 1) {
    return entity.validationDate ?? undefined
  }
  return entity.lastRelevancyDate ?? entity.enrichmentRelevancyDate ?? undefined
}

/**
 * `shouldShowLastUpdatedIcon` — the line appears only while the validation is still recent,
 * where "recent" is `window.careero.relevantDaysForEnriched` days.
 *
 * ⚠️ AN ABSENT `relevantDaysForEnriched` HIDES THE LINE RATHER THAN SHOWING IT ALWAYS, and
 * that is upstream's behaviour rather than a guard added here: `setDate(getDate() - undefined)`
 * yields an Invalid Date, whose `getTime()` is NaN, and `date >= NaN` is false. So a tenant
 * whose `/extension/view-model` does not serve the field renders exactly what this does. The
 * explicit check is here so that reading the code says so; deleting it changes nothing.
 */
export function shouldShowLastUpdatedIcon(
  entity: EnrichableEntity | undefined,
  relevantDaysForEnriched: number | undefined,
  now: number = Date.now(),
): boolean {
  // The component is shared with the external-source snippet, which has no alert entity at all.
  if (entity === undefined) return false
  if (relevantDaysForEnriched === undefined) return false

  const validationDate = validationDateWithFallback(entity)
  if (validationDate === undefined || validationDate === 0) return false

  const minDaysAgo = new Date(now)
  minDaysAgo.setDate(minDaysAgo.getDate() - relevantDaysForEnriched)
  return validationDate >= minDaysAgo.getTime()
}
